import { StatusCodes } from '@/composables/api'
import { Post } from '@/model/data-object/Post'
import useCurrentUser from '@/composables/user/user'
import NotLoggedIn from '@/error/notLoggedIn'
import { ref, Ref } from 'vue'
import useAnalyticsManager, {
  ANALYTICS_EVENT_POST_EDIT_SAVE,
  ANALYTICS_EVENT_POST_VOTE_SUPER_LIKE,
  ANALYTICS_EVENT_POST_VOTE_SUPER_LIKE_CONFIRMATION,
} from '@/composables/analytics/analyticsManager'
import usePostApi from '@/composables/post/postApi'
import { TOOLTIP_COMPONENTS } from '@/composables/post/postTooltip'

export default function usePostSuperLike(post: Post) {
  const { postSuperLike } = usePostApi()
  const { currentUser } = useCurrentUser()
  const { propagateAnalyticsEvent } = useAnalyticsManager()
  const postSuperLikeAlreadyPerformed: Ref<boolean> = ref(false)
  const calledSuperLikeAction: Ref<boolean> = ref(false)
  const {
    loading: isPerformingSuperLike,
    request,
    response,
    statusCode,
  } = postSuperLike(post.id)
  const { loggedIn } = useCurrentUser()

  const superLike = async () => {
    if (loggedIn.value) {
      isPerformingSuperLike.value = true
      propagateAnalyticsEvent(
        post,
        ANALYTICS_EVENT_POST_VOTE_SUPER_LIKE_CONFIRMATION
      )
      await request()
      isPerformingSuperLike.value = false
      calledSuperLikeAction.value = true

      if (statusCode.value === StatusCodes.OK) {
        postSuperLikeAlreadyPerformed.value = true
      } else if (statusCode.value === StatusCodes.CREATED) {
        postSuperLikeAlreadyPerformed.value = false
        if (currentUser.value && currentUser.value?.stats) {
          currentUser.value.stats.superLikesRemaining--
        }
      }

      if (response.value) {
        post.stats.superLikes = response.value.post.stats.superLikes
      }

      return
    }
    throw new NotLoggedIn('postVote')
  }

  return {
    isPerformingSuperLike,
    postSuperLikeAlreadyPerformed,
    calledSuperLikeAction,
    superLike,
  }
}

export function usePostSuperLikeTooltip(
  post: Post,
  currentComponent: Ref<TOOLTIP_COMPONENTS | undefined>
) {
  const { currentUser } = useCurrentUser()
  const { propagateAnalyticsEvent } = useAnalyticsManager()
  const postSuperLikeQuestion = () => {
    if (
      typeof currentComponent.value !== 'undefined' &&
      [
        TOOLTIP_COMPONENTS.POST_SUPER_LIKE_LOGIN,
        TOOLTIP_COMPONENTS.POST_SUPER_LIKE_QUOTA,
        TOOLTIP_COMPONENTS.POST_SUPER_LIKE_UPGRADE,
        TOOLTIP_COMPONENTS.POST_SUPER_LIKE,
        TOOLTIP_COMPONENTS.POST_SUPER_LIKE_BUY_SUBSCRIPTION,
      ].includes(currentComponent.value)
    ) {
      currentComponent.value = undefined

      return
    }
    if (typeof currentUser.value === 'undefined') {
      currentComponent.value = TOOLTIP_COMPONENTS.POST_SUPER_LIKE_LOGIN

      return
    }
    if (false === currentUser.value?.isPayingCustomer) {
      currentComponent.value =
        TOOLTIP_COMPONENTS.POST_SUPER_LIKE_BUY_SUBSCRIPTION

      return
    }
    if (currentUser.value?.stats.superLikesRemaining <= 0) {
      if (currentUser.value?.isSubscriptionPremium) {
        currentComponent.value = TOOLTIP_COMPONENTS.POST_SUPER_LIKE_QUOTA

        return
      }
      currentComponent.value = TOOLTIP_COMPONENTS.POST_SUPER_LIKE_UPGRADE

      return
    }
    propagateAnalyticsEvent(post, ANALYTICS_EVENT_POST_VOTE_SUPER_LIKE)

    currentComponent.value = TOOLTIP_COMPONENTS.POST_SUPER_LIKE
  }

  return {
    postSuperLikeQuestion,
  }
}
